"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { supabaseClient } from "@/lib/supabase/client"

interface Casamento {
  id: string
  nome_casal: string
  slug: string
  nome_noiva: string
  nome_noivo: string
  data_casamento: string
  status: string
  cor_primaria: string
  cor_secundaria: string
}

interface CasamentoContextType {
  casamento: Casamento | null
  loading: boolean
  setCasamentoAtivo: (id: string) => Promise<void>
  casamentos: Casamento[]
  reloadCasamentos: () => Promise<void>
}

const CasamentoContext = createContext<CasamentoContextType>({
  casamento: null,
  loading: true,
  setCasamentoAtivo: async () => {},
  casamentos: [],
  reloadCasamentos: async () => {},
})

export function CasamentoProvider({ children }: { children: ReactNode }) {
  const [casamento, setCasamento] = useState<Casamento | null>(null)
  const [casamentos, setCasamentos] = useState<Casamento[]>([])
  const [loading, setLoading] = useState(true)

  // Carregar casamento ativo
  useEffect(() => {
    // Prioridade 1: Variável de ambiente NEXT_PUBLIC_CASAMENTO_ID (para site público no Vercel)
    const casamentoIdEnv = process.env.NEXT_PUBLIC_CASAMENTO_ID
    if (casamentoIdEnv) {
      loadCasamento(casamentoIdEnv)
      return
    }
    
    // Prioridade 2: localStorage (para área admin)
    const casamentoId = localStorage.getItem("casamento_ativo_id")
    if (casamentoId) {
      loadCasamento(casamentoId)
    } else {
      // Prioridade 3: buscar o primeiro casamento do usuário
      loadFirstCasamento()
    }
  }, [])

  const loadCasamento = async (id: string) => {
    try {
      const { data, error } = await supabaseClient
        .from("casamentos")
        .select("*")
        .eq("id", id)
        .single()

      if (error) {
        console.error("Erro ao carregar casamento:", error)
        setLoading(false)
        return
      }

      if (data) {
        setCasamento(data)
        localStorage.setItem("casamento_ativo_id", data.id)
      }
    } catch (error) {
      console.error("Erro ao carregar casamento:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadFirstCasamento = async () => {
    try {
      // Buscar email do usuário logado
      const userEmail = sessionStorage.getItem("userEmail")
      if (!userEmail) {
        setLoading(false)
        return
      }

      // Buscar ID do usuário
      const { data: user, error: userError } = await supabaseClient
        .from("admin_users")
        .select("id")
        .eq("email", userEmail)
        .single()

      if (userError || !user) {
        console.error("Usuário não encontrado:", userError)
        setLoading(false)
        return
      }

      // Buscar primeiro casamento do usuário
      const { data: casamentoUsuario, error: casamentoError } = await supabaseClient
        .from("casamento_usuarios")
        .select("casamento_id")
        .eq("usuario_id", user.id)
        .limit(1)
        .single()

      if (casamentoError && casamentoError.code !== "PGRST116") {
        console.error("Erro ao buscar casamento do usuário:", casamentoError)
      }

      if (casamentoUsuario) {
        await loadCasamento(casamentoUsuario.casamento_id)
      } else {
        // Se não encontrou na tabela casamento_usuarios,
        // buscar o primeiro casamento disponível (para compatibilidade)
        const { data: primeiroCasamento } = await supabaseClient
          .from("casamentos")
          .select("*")
          .eq("status", "ativo")
          .limit(1)
          .single()

        if (primeiroCasamento) {
          await loadCasamento(primeiroCasamento.id)
        } else {
          setLoading(false)
        }
      }
    } catch (error) {
      console.error("Erro ao carregar primeiro casamento:", error)
      setLoading(false)
    }
  }

  const reloadCasamentos = async () => {
    try {
      const userEmail = sessionStorage.getItem("userEmail")
      if (!userEmail) return

      const { data: user } = await supabaseClient
        .from("admin_users")
        .select("id, papel")
        .eq("email", userEmail)
        .single()

      if (!user) return

      let query = supabaseClient.from("casamentos").select("*").order("created_at", { ascending: false })

      // Se não for super admin, filtrar apenas seus casamentos
      if (user.papel !== "super_admin") {
        const { data: userCasamentos } = await supabaseClient
          .from("casamento_usuarios")
          .select("casamento_id")
          .eq("usuario_id", user.id)

        const ids = userCasamentos?.map((c) => c.casamento_id) || []
        if (ids.length > 0) {
          query = query.in("id", ids)
        } else {
          // Se não tem casamentos, retornar vazio
          setCasamentos([])
          return
        }
      }

      const { data } = await query
      setCasamentos(data || [])
    } catch (error) {
      console.error("Erro ao recarregar casamentos:", error)
    }
  }

  useEffect(() => {
    if (!loading) {
      reloadCasamentos()
    }
  }, [loading])

  const setCasamentoAtivo = async (id: string) => {
    await loadCasamento(id)
    // Recarregar a página para aplicar as mudanças
    if (typeof window !== "undefined") {
      window.location.reload()
    }
  }

  return (
    <CasamentoContext.Provider
      value={{
        casamento,
        loading,
        setCasamentoAtivo,
        casamentos,
        reloadCasamentos,
      }}
    >
      {children}
    </CasamentoContext.Provider>
  )
}

export const useCasamento = () => {
  const context = useContext(CasamentoContext)
  if (!context) {
    throw new Error("useCasamento deve ser usado dentro de CasamentoProvider")
  }
  return context
}
